import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError, AxiosResponse } from "axios";

import { api } from "@/shared/api";
import type {
  NotificationMailRequest,
  PushLeadNotificationRequest,
  PushWorkerNotificationRequest,
  ReadNotificationRequest,
  VerdictNotificationRequest,
} from "./api.dto";
import { useApi } from "@/shared/api/api";
import { useToast } from "@/shared/hooks/useToast";
import { useSessionStore } from "@/entities/session";
import { useMutate } from "@/shared/hooks";
import { filterObjectKeys } from "@/shared/lib/utils";
import { type PaginationRequest } from "@/shared/types";

interface BroadcastToLeadsRequest {
  message: string;
}

export const useVerdictNotification = () => {
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse,
    AxiosError,
    VerdictNotificationRequest,
    Response
  >({
    mutationKey: ["verdictNotification"],
    mutationFn: (data) => {
      return api.put(`/notification/verdict/${data.id}`, {
        verdict: data.verdict,
      });
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ["unreadNotifications"] });
      queryClient.refetchQueries({ queryKey: ["notifications"] });
    },
  });
};

export const useNotifications = (
  props: PaginationRequest & { isArchived: boolean | null },
) => {
  const { api } = useApi();
  const params = filterObjectKeys(props);

  return useQuery({
    queryKey: ["notifications", params],
    queryFn: async () => {
      const response = await api.get("/notification", {
        params,
      });
      return response.data;
    },
  });
};

export const useReadAllNotifications = () => {
  const { api } = useApi();

  const { accessToken } = useSessionStore();
  return useQuery<AxiosResponse>({
    queryKey: ["readNotifications"],
    queryFn: async () => {
      const response = await api.put("/notification/archived/all");
      return response.data;
    },
    enabled: !!accessToken,
  });
};

export const useUnreadNotificationsCount = () => {
  const { api } = useApi();

  const { accessToken } = useSessionStore();
  return useQuery<AxiosResponse>({
    queryKey: ["unreadNotifications"],
    queryFn: async () => {
      const response = await api.get("/notification/unread");
      return response.data;
    },
    enabled: !!accessToken,
  });
};

export const useReadNotification = () => {
  const queryClient = useQueryClient();
  const { api } = useApi();
  return useMutation<
    AxiosResponse,
    AxiosError,
    ReadNotificationRequest,
    Response
  >({
    mutationKey: ["readNotification"],
    mutationFn: (data) => {
      return api.put(`/notification/archived/${data.id}`);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ["unreadNotifications"] });
      queryClient.refetchQueries({ queryKey: ["notifications"] });
    },
  });
};

export const useSendPushWorker = () => {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<
    AxiosResponse,
    AxiosError,
    PushWorkerNotificationRequest,
    Response
  >({
    mutationKey: ["sendPushWorker"],
    mutationFn: (data) => {
      return api.post("/notification/push/worker", data);
    },
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ["leadsByCurator"],
      });
      queryClient.refetchQueries({
        queryKey: ["allCuratorsLeads"],
      });

      toast({
        title: "Удачно!",
        description: "Вы отправили уведомление воркеру.",
      });
    },
  });
};

export const useSendPushLead = () => {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<
    AxiosResponse,
    AxiosError,
    PushLeadNotificationRequest,
    Response
  >({
    mutationKey: ["sendPushLead"],
    mutationFn: (data) => {
      return api.post("/notification/push/lead", data);
    },
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ["leadsByCurator"],
      });
      queryClient.refetchQueries({
        queryKey: ["allCuratorsLeads"],
      });
      toast({
        title: "Удачно!",
        description: "Вы отправили уведомление  лиду.",
      });
    },
  });
};

export const useOutdatedContacts = () => {
  const { mutationFn, onSuccess, onError } = useMutate({
    method: "post",
    url: "/notification/outdated-contacts",
    toast: {
      success: {
        description: "Вы отправили уведомление наставнику.",
      },
    },
  });

  return useMutation<AxiosResponse, AxiosError>({
    mutationKey: ["outdatedContacts"],
    mutationFn,
    onSuccess,
    onError,
  });
};

export const useBroadcastToLeads = () => {
  const { mutationFn, onSuccess, onError } = useMutate({
    method: "post",
    url: "/notification/broadcast/leads",
    toast: {
      success: {
        description: "Вы разослали сообщение.",
      },
    },
  });

  return useMutation<
    AxiosResponse,
    AxiosError,
    BroadcastToLeadsRequest,
    Response
  >({
    mutationKey: ["broadcastToLeads"],
    mutationFn,
    onSuccess,
    onError,
  });
};

export const useNotificationBanner = () => {
  const { mutationFn, onSuccess, onError } = useMutate({
    method: "post",
    url: "/notification/banner",
    toast: {
      success: {
        description: "Вы разослали сообщение с баннером.",
      },
    },
  });

  return useMutation<
    AxiosResponse,
    AxiosError,
    BroadcastToLeadsRequest,
    Response
  >({
    mutationKey: ["notificationBanner"],
    mutationFn,
    onSuccess,
    onError,
  });
};

export const useNotificationMail = () => {
  const { onSuccess, onError } = useMutate({
    method: "post",
    url: "/notification/broadcast",
    toast: {
      success: {
        description: "Вы разослали сообщение.",
      },
    },
  });
  const { api } = useApi();

  return useMutation<AxiosResponse, AxiosError, NotificationMailRequest>({
    mutationKey: ["notificationMail"],
    mutationFn: (data) =>
      api.post("/notification/broadcast", data, {
        headers: { "Content-Type": "multipart/form-data" },
      }),
    onSuccess,
    onError,
  });
};
